import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box/Box';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import TextField from '@mui/material/TextField/TextField';
import Button from '@mui/material/Button/Button';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Alert from '@mui/material/Alert/Alert';
import Avatar from '@mui/material/Avatar/Avatar';
import List from '@mui/material/List/List';
import ListItem from '@mui/material/ListItem/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText/ListItemText';
import IconButton from '@mui/material/IconButton/IconButton';
import Snackbar from '@mui/material/Snackbar/Snackbar';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import BusinessIcon from '@mui/icons-material/Business';
import { PageContainer } from '../../components/PageContainer';
import { OrganizationEvents } from '../../components/Organization/OrganizationEvents';
import { Loading } from '../../components/Loading';
import { BrandingEditor } from '../../components/Branding/BrandingEditor';
import { Branding } from '../../services/branding.service';
import {
    addOrganizationAdmin,
    deleteMyOrganization,
    getOrganization,
    OrganizationDetail,
    removeOrganizationAdmin,
    updateOrganization
} from '../../services/organization.service';
import { refreshMyOrganizations } from '../../hooks/useMyOrganizations';
import { useAuth } from '../../services/useAuth';

const errorMessage = (err: any, fallback: string) => err?.response?.data?.message || fallback;

export const OrganizationSettings = () => {
    const { id } = useParams();
    const { user } = useAuth();

    const [organization, setOrganization] = useState<OrganizationDetail | null>(null);
    const [loadError, setLoadError] = useState('');
    const [name, setName] = useState('');
    const [domains, setDomains] = useState('');
    const [branding, setBranding] = useState<Branding>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saved, setSaved] = useState(false);
    const [newAdmin, setNewAdmin] = useState('');
    const [accessDomain, setAccessDomain] = useState('');
    const [accessAuth, setAccessAuth] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteName, setDeleteName] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const navigate = useNavigate();
    const [adminError, setAdminError] = useState('');

    const isPlatformAdmin = !!user && user.isAdmin;

    const applyOrganization = (org: OrganizationDetail) => {
        setOrganization(org);
        setName(org.name);
        setDomains(org.domains.join(', '));
        setBranding(org.branding || {});
        setAccessDomain(org.access?.domain || '');
        setAccessAuth(org.access?.preferredAuth || '');
    }

    useEffect(() => {
        if (!id) {
            return;
        }
        getOrganization(id).then(applyOrganization, (err) => {
            setLoadError(err.response?.status === 403
                ? 'Only organization admins can manage these settings.'
                : errorMessage(err, 'Could not load the organization'));
        });
    }, [id]);

    if (loadError) {
        return <PageContainer><Alert severity="error">{loadError}</Alert></PageContainer>
    }

    if (!organization) {
        return <Loading />
    }

    const save = () => {
        setSaving(true);
        setSaveError('');
        updateOrganization(organization._id, {
            name,
            branding,
            access: { domain: accessDomain, preferredAuth: accessAuth },
            ...(isPlatformAdmin ? { domains: domains.split(',').map(d => d.trim()).filter(Boolean) } : {})
        }).then(org => {
            applyOrganization(org);
            setSaved(true);
            refreshMyOrganizations();
        }, (err) => {
            setSaveError(errorMessage(err, 'Could not save'));
        }).finally(() => {
            setSaving(false);
        });
    }

    const addAdmin = () => {
        setAdminError('');
        addOrganizationAdmin(organization._id, newAdmin).then(org => {
            setOrganization(org);
            setNewAdmin('');
        }, (err) => {
            setAdminError(errorMessage(err, 'Could not add admin'));
        });
    }

    const removeAdmin = (email: string) => {
        setAdminError('');
        removeOrganizationAdmin(organization._id, email).then(org => {
            setOrganization(org);
            refreshMyOrganizations();
        }, (err) => {
            setAdminError(errorMessage(err, 'Could not remove admin'));
        });
    }

    return (
        <PageContainer variant="plain">
            <Stack spacing={3} sx={{ maxWidth: 960, mx: 'auto' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'primary.main' }} src={organization.branding?.logoUrl}>
                        <BusinessIcon />
                    </Avatar>
                    <div>
                        <Typography variant="overline" color="text.secondary">Organization settings</Typography>
                        <Typography variant="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' } }}>{organization.name}</Typography>
                    </div>
                </Stack>

                <Card>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={2.5}>
                            <Typography variant="h4" component="h2">Profile</Typography>
                            <TextField label="Name" value={name} onChange={e => setName(e.target.value)} sx={{ maxWidth: 420 }} />
                            {
                                isPlatformAdmin ? (
                                    <TextField
                                        label="Membership domains (platform admins only)"
                                        value={domains}
                                        helperText="Comma separated. Anyone signed in with these domains is a member: they can link hackathons to this organization."
                                        onChange={e => setDomains(e.target.value)} />
                                ) : null
                            }
                        </Stack>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={2}>
                            <div>
                                <Typography variant="h4" component="h2">Who can join</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    The standard for new hackathons created in {organization.name}. Organizers start with it and can change it for one event.
                                </Typography>
                            </div>
                            <TextField label="Email domain" value={accessDomain} placeholder="e.g. acme.com" sx={{ maxWidth: 420 }}
                                helperText={accessDomain.trim() ? `Only people signed in with an @${accessDomain.trim().replace(/^@/, '')} email can join.` : 'Anyone with the link (and invite code, if set) can join.'}
                                onChange={e => setAccessDomain(e.target.value)} />
                            <TextField select label="Suggested sign-in" value={accessAuth} sx={{ maxWidth: 420 }} onChange={e => setAccessAuth(e.target.value)}
                                SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                                <MenuItem value="">Any</MenuItem>
                                <MenuItem value="google">Google</MenuItem>
                            </TextField>
                        </Stack>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={2.5}>
                            <div>
                                <Typography variant="h4" component="h2">Branding</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Applied to every <b>Pro</b> hackathon linked to {organization.name} (<a href="/pricing">per-event pricing</a>). Organizers can override any field per hackathon.
                                </Typography>
                            </div>
                            <BrandingEditor value={branding} onChange={setBranding} previewName="Your next hackathon" />
                        </Stack>
                    </CardContent>
                </Card>

                <Stack spacing={2}>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                    <Stack direction="row">
                        <Button variant="contained" size="large" disabled={saving} onClick={save}>Save changes</Button>
                    </Stack>
                </Stack>

                <Card>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={2}>
                            <div>
                                <Typography variant="h4" component="h2">Admins</Typography>
                                <Typography variant="body2" color="text.secondary">Admins can change this organization's name and branding.</Typography>
                            </div>
                            <List disablePadding>
                                {
                                    organization.admins.map(admin => (
                                        <ListItem key={admin._id} disableGutters secondaryAction={
                                            <Tooltip title={organization.admins.length <= 1 ? 'The only admin can\'t leave: add another admin first, or delete the organization' : 'Remove admin'}>
                                                {/* a span, so the tooltip still shows on the disabled button */}
                                                <span>
                                                    <IconButton edge="end" aria-label={`Remove admin: ${admin.email}`} disabled={organization.admins.length <= 1}
                                                        onClick={() => removeAdmin(admin.email)}><PersonRemoveIcon /></IconButton>
                                                </span>
                                            </Tooltip>
                                        }>
                                            <ListItemAvatar>
                                                <Avatar>{(admin.username || admin.email).charAt(0).toUpperCase()}</Avatar>
                                            </ListItemAvatar>
                                            <ListItemText primary={admin.username} secondary={admin.email} />
                                        </ListItem>
                                    ))
                                }
                            </List>
                            <Stack direction="row" spacing={1}>
                                <TextField size="small" label="Add admin by email" value={newAdmin} onChange={e => setNewAdmin(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && newAdmin) { addAdmin(); } }} sx={{ minWidth: 280 }} />
                                <Button variant="outlined" disabled={!newAdmin} onClick={addAdmin}>Add</Button>
                            </Stack>
                            {adminError && <Alert severity="error">{adminError}</Alert>}
                        </Stack>
                    </CardContent>
                </Card>
                <OrganizationEvents organizationId={organization._id} />

                <Card variant="outlined" sx={{ borderColor: 'error.light' }}>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={1.5}>
                            <Typography variant="h4" component="h2">Delete organization</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Removes {organization.name}, its branding, access rule and admins. Its hackathons keep running, unlinked: they keep
                                their own settings and lose the organization's branding. This can't be undone.
                            </Typography>
                            {
                                // can't be undone, so this one asks: type the name to confirm
                                !confirmDelete
                                    ? <Box><Button color="error" variant="outlined" onClick={() => { setConfirmDelete(true); setDeleteName(''); setDeleteError(''); }}>Delete organization</Button></Box>
                                    : <Stack spacing={1} sx={{ maxWidth: 420 }}>
                                        <TextField size="small" autoFocus label={`Type "${organization.name}" to confirm`} value={deleteName} onChange={e => setDeleteName(e.target.value)} />
                                        <Stack direction="row" spacing={1}>
                                            <Button color="error" variant="contained" disabled={deleteName.trim() !== organization.name.trim()}
                                                onClick={() => {
                                                    setDeleteError('');
                                                    deleteMyOrganization(organization._id).then(() => {
                                                        refreshMyOrganizations();
                                                        navigate('/');
                                                    }, err => setDeleteError(errorMessage(err, 'Could not delete the organization')));
                                                }}>Delete {organization.name}</Button>
                                            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                                        </Stack>
                                        {deleteError && <Alert severity="error">{deleteError}</Alert>}
                                    </Stack>
                            }
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
            <Snackbar open={saved} autoHideDuration={4000} onClose={() => setSaved(false)} message="Organization saved" />
        </PageContainer>
    )
}
